import { useEffect } from "react";
import { useAppSelector } from "../store/hooks";
import { refreshSession } from "../services/api";
import { isAccessNearExpiry } from "../services/sessionRefresh";

/** How often to check whether the access token is about to expire. */
export const REFRESH_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Keeps the session alive while the app is open. Refreshes only when the
 * access token is about to expire (timestamp shared by all tabs) and always
 * through the coordinated `refreshSession`, so the interval, tab switches
 * and the 401 interceptor never send the same refresh cookie twice.
 * A failed refresh shows the re-login dialog (sessionEvents.ts); nothing
 * here touches the Redux auth state, so open forms do not re-render.
 */
export const useTokenRefresh = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshIfNeeded = () => {
      if (!isAccessNearExpiry()) return;
      refreshSession().catch(() => {
        // Session loss is reported through sessionEvents; network errors
        // are retried on the next check or by the 401 interceptor.
      });
    };

    const intervalId = setInterval(refreshIfNeeded, REFRESH_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshIfNeeded();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthenticated]);
};
